import React, { useEffect } from 'react';

const ThermalReceipt = ({ receiptData, onClose, autoPrint = false, paperWidth = '80mm' }) => {
  // Disabled auto-print by default - user must click Print button
  useEffect(() => {
    if (autoPrint && receiptData) {
      // Small delay to ensure content is rendered
      const printTimer = setTimeout(() => {
        window.print();
      }, 500);

      return () => clearTimeout(printTimer);
    }
  }, [autoPrint, receiptData]);

  if (!receiptData) return null;

  const handlePrint = () => {
    window.print();
  };

  const handleSavePDF = () => {
    // Trigger print dialog which allows "Save as PDF" option
    window.print();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const baseFont = paperWidth === '50mm' ? '9px' : paperWidth === '58mm' ? '10px' : paperWidth === '72mm' ? '11px' : '12px';
  const qrSize = paperWidth === '50mm' ? 100 : paperWidth === '58mm' ? 120 : paperWidth === '72mm' ? 140 : 160;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 print:bg-white">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 print:shadow-none print:max-w-full">
        {/* Screen View - Not printed */}
        <div className="print:hidden p-4 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-lg font-bold">Receipt Preview</h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
        </div>

        {/* Receipt Content - Thermal Printer Style */}
        <div className="thermal-receipt p-6 print:p-0">
          <style>{`
            @media print {
              /* Reset all page margins and padding */
              @page {
                size: ${paperWidth} auto;
                margin: 0;
                padding: 0;
              }
              
              html, body {
                width: ${paperWidth};
                margin: 0;
                padding: 0;
              }
              
              body * {
                visibility: hidden;
              }
              
              .thermal-receipt,
              .thermal-receipt * {
                visibility: visible;
              }
              
              .thermal-receipt {
                position: absolute;
                left: 0;
                top: 0;
                width: ${paperWidth};
                max-width: ${paperWidth};
                font-family: 'Courier New', monospace;
                font-size: ${baseFont};
                line-height: 1.25;
                padding: 4mm;
                margin: 0;
                box-sizing: border-box;
                color: #000;
              }
              
              .no-print {
                display: none !important;
              }
            }
            
            .thermal-receipt {
              font-family: 'Courier New', monospace;
              max-width: ${paperWidth};
              margin: 0 auto;
              font-size: ${baseFont};
            }
          `}</style>

          {/* Compact Header */}
          <div className="text-center border-b border-dashed border-gray-400 pb-2 mb-2">
            <div className="text-lg font-bold">ROAD FUND</div>
            <div className="text-xs font-bold">{receiptData.paymentMethod === 'cash' ? '💵 CASH TOP-UP' : '📱 QR TOP-UP'}</div>
            {receiptData.qrImage && (
              <div className="my-2">
                <img src={receiptData.qrImage} alt="QR" style={{ width: qrSize + 'px', height: qrSize + 'px', display: 'block', margin: '6px auto' }} />
              </div>
            )}
          </div>

          {/* Compact Transaction Details */}
          <div className="space-y-0.5 text-xs mb-2">
            <div className="flex justify-between">
              <span>{formatDate(receiptData.timestamp || new Date())}</span>
            </div>

            <div className="border-t border-dashed border-gray-300 my-1"></div>

            {receiptData.licensePlate && (
              <div className="flex justify-between font-bold">
                <span>🚗 ທະບຽນລົດ:</span>
                <span>{receiptData.licensePlate}</span>
              </div>
            )}

            <div className="flex justify-between">
              <span>ບັດ:</span>
              <span className="font-mono">{receiptData.cardUid}</span>
            </div>

            <div className="border-t border-dashed border-gray-300 my-1"></div>

            {receiptData.previousBalance !== undefined && (
              <div className="flex justify-between">
                <span>ຄ່າມັດຈຳ/ຍອດເກົ່າ:</span>
                <span>{receiptData.previousBalance.toLocaleString()} LAK</span>
              </div>
            )}

            <div className="flex justify-between font-bold text-sm">
              <span>ຈຳນວນເຕີມ:</span>
              <span>+{receiptData.amount.toLocaleString()} LAK</span>
            </div>

            {receiptData.newBalance !== undefined && (
              <div className="flex justify-between text-base font-bold bg-green-100 p-1 rounded">
                <span>ມູນຄ່າທີນຳໃຊ້ໄດ້:</span>
                <span>{receiptData.newBalance.toLocaleString()} LAK</span>
              </div>
            )}

            <div className="border-t border-dashed border-gray-300 my-1"></div>

            <div className="flex justify-between">
              <span>ລະຫັດທຸລະກຳ:</span>
            </div>
            <div className="font-mono text-xs break-all bg-gray-100 p-0.5">
              {receiptData.transactionId}
            </div>

            {receiptData.reference2 && (
              <div className="flex justify-between">
                <span>ເລກທີອ້າງອິງ:</span>
                <span className="font-mono text-xs">{receiptData.reference2}</span>
              </div>
            )}

            <div className="flex justify-between mt-1">
              <span>ສະຖານະ:</span>
              <span className="text-green-600 font-bold">✓ ສຳເລັດ</span>
            </div>
          </div>

          {/* Compact Footer */}
          <div className="border-t border-dashed border-gray-400 pt-1 mt-2 text-center text-xs">
            <div>ຂອບໃຈທີ່ໃໍ້ຊ້ບໍລິການ Thank you!</div>
            <div className="text-xs text-gray-400 mt-1">* * * * * * * *</div>
          </div>
        </div>

        {/* Action Buttons - Not printed */}
        <div className="print:hidden p-4 border-t flex gap-2">
          <button
            onClick={handlePrint}
            className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded transition-colors text-sm"
          >
            🖨️ Print
          </button>
          <button
            onClick={handleSavePDF}
            className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded transition-colors text-sm"
          >
            💾 Save PDF
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded transition-colors text-sm"
          >
            ❌ Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ThermalReceipt;
